import * as React           from "react";
import Button               from "@mui/material/Button";
import CircularProgress     from "@mui/material/CircularProgress";
import Typography           from "@mui/material/Typography";
import Delete               from "@mui/icons-material/Delete";
import Edit                 from "@mui/icons-material/Edit";
import Check                from "@mui/icons-material/Check";
import Close                from "@mui/icons-material/Close";
import Popover              from "@mui/material/Popover";
import {
    DataGrid,
    GridRowParams,
    GridActionsCellItem,
    GridEditRowsModel,
    GridRenderCellParams,
    GridApi,
    GridRowId,
    GridColumns,
}                           from "@mui/x-data-grid";
import {
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
}                           from "react-query";
import { HttpClient }       from "apiclient/ApiClient";
import IStudent             from "models/IStudent";
import DeleteStudentDialog  from "ui/components/students-components/DeleteStudentDialog";
import GridDatePicker       from 'ui/components/students-components/GridDatePicker';
import IProfile             from 'models/IProfile';
import {
    STUDENTS_TABLE_CANCEL,
    STUDENTS_TABLE_DATA_ERROR,
    STUDENTS_TABLE_DATE,
    STUDENTS_TABLE_DELETE,
    STUDENTS_TABLE_EDIT,
    STUDENTS_TABLE_EMAIL,
    STUDENTS_TABLE_EMAIL_ERROR,
    STUDENTS_TABLE_EMPTY,
    STUDENTS_TABLE_NAME,
    STUDENTS_TABLE_NUM,
    STUDENTS_TABLE_SAVE
}                           from "config/config";
import convertDate          from "utils/convertDate";
import validateEmail from "utils/validateEmail";
//import useLocalStorage from "hooks/useLocalStorage";
//import ISession from "models/ISession";

function setProperty<T, K extends keyof T>(obj: T, key: K, value: T[K]) {
    obj[key] = value;
};

const renderDate = (params: GridRenderCellParams) => {
    return <Typography variant="body2">{convertDate(params?.value)}</Typography>;
};

const data_columns: GridColumns = [
    {   field: 'id',
        headerName: 'ID',
        editable: false,
        hide: true
    },
    {   field: 'num',
        headerName: STUDENTS_TABLE_NUM,
        editable: false,
        minWidth: 40,
        flex: 0.3,
        hideSortIcons: true,
        disableColumnMenu: true
    },
    {   field: 'first_name',
        headerName: STUDENTS_TABLE_NAME,
        minWidth: 120,
        flex: 1,
        editable: true,
        disableColumnMenu: true
    },
    {   field: 'date_of_birth',
        headerName: STUDENTS_TABLE_DATE,
        minWidth: 120,
        flex: 1,
        editable: true,
        type: 'date',
        disableColumnMenu: true,
        renderEditCell: GridDatePicker,
        renderCell: renderDate
    },
    {   field: 'parent_email',
        headerName: STUDENTS_TABLE_EMAIL,
        minWidth: 160,
        flex: 1,
        editable: true,
        type: 'string',
        disableColumnMenu: true
    },
];

interface StudentsTableProps {
    setSyncState: React.Dispatch<React.SetStateAction<boolean>>;
    setStudentsPresent?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsAddStudentVisible: React.Dispatch<React.SetStateAction<boolean>>;
    classId?: string;
    source?: string;
};

function putStudent (
    id: GridRowId,
    model: GridEditRowsModel,
    studentPut: UseMutationResult<void, unknown, IStudent, unknown>
): boolean 
{
    let student: IStudent = {
        id: id as string,
        first_name: '',
        date_of_birth: '',
        parent_email: ''
    };
    let key: string;
    for (key in model[id]) {
        if (key in student) {
            setProperty(student, key as keyof IStudent, model[id][key].value as string);
        };
    };
    if (student.date_of_birth && student.first_name) {
        studentPut.mutate(student);
        return true;
    };
    return false;
};

export default function StudentsTable (props: StudentsTableProps): JSX.Element {
    const httpClient = HttpClient();
    const queryClient = useQueryClient();
    //const [session] = useLocalStorage<ISession>('session', {uid: ''})
    const invalidate = () => {
        queryClient.invalidateQueries(`classes/${props.classId}`);
    };
    const studentsGet: UseQueryResult<IProfile[]> = useQuery(
        `classes/${props.classId}`,
        async () => await httpClient.get(`/school/classes/${props.classId}/members`)
    );
    const studentPut = useMutation(
        async (data: IStudent) => {
            await httpClient.put(`classes/${props.classId}/profile/${data.id}`, data);
        },
        {onSettled: () => invalidate()}
    );
    const studentDelete = useMutation(
        async (id: string) => {
            await httpClient.delete(`classes/${props.classId}/profile/${id}`);
        },
        {onSettled: () => invalidate()}
    );
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [cellError, setcellError] = React.useState('');
    const [editRowsModel, setEditRowsModel] = React.useState({} as GridEditRowsModel);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [idToDelete, setIdToDelete] = React.useState('');

    function getStudentName(id: string): string {
        const students = studentsGet?.data?.filter(student => student.id === id)
        if (students && students.length > 0) {
            return students[0].first_name + ' ' + students[0].last_name;
        }
        return '';
    };

    function useApiRef() {
        const apiRef = React.useRef({} as GridApi);
        const _columns = data_columns.concat([
            {
                field: 'actions',
                type: 'actions',
                flex: 0.3,
                minWidth: 70,
                align: 'center',
                hideSortIcons: true,
                disableColumnMenu: true,
                getActions: (params: GridRowParams) => {
                    if (props.source !== 'teacher') {
                        return [];
                    } else if (apiRef.current.getRowMode(params.id) === 'view') {
                        return([
                            <GridActionsCellItem
                                icon={<Delete titleAccess={STUDENTS_TABLE_DELETE} />}
                                onClick={() => {
                                    setIdToDelete(params.getValue(params.id, 'id') as string);
                                    setDialogOpen(true);
                                    //studentDelete.mutate(params.getValue(params.id, 'id') as string);
                                }}
                                label={STUDENTS_TABLE_DELETE}
                            />,
                            <GridActionsCellItem
                                sx={{mr: 2}}
                                icon={<Edit titleAccess={STUDENTS_TABLE_EDIT} />}
                                onClick={() => {
                                    apiRef.current.setRowMode(params.id, "edit")
                                }}
                                label={STUDENTS_TABLE_EDIT}
                            />,
                        ])
                    } else {
                        return([
                            <GridActionsCellItem
                                sx={{ml: 1}}
                                icon={<Check titleAccess={STUDENTS_TABLE_SAVE} />}
                                onClick={() => {
                                    if (putStudent(params.id, editRowsModel, studentPut)) {
                                        apiRef.current.setRowMode(params.id, "view");
                                    };
                                }}
                                label={STUDENTS_TABLE_SAVE}
                            />,
                            <GridActionsCellItem
                                icon={<Close titleAccess={STUDENTS_TABLE_CANCEL} />}
                                onClick={() => {
                                    apiRef.current.setRowMode(params.id, "view")
                                }}
                                label={STUDENTS_TABLE_CANCEL}
                            />,
                        ])
                    }
                }
            },
            {   field: ".",
                width: 0,
                minWidth: 0,
                flex: 0,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (
                    (params: GridRenderCellParams) => {apiRef.current = params.api as GridApi}
                ) as ((params: GridRenderCellParams) => React.ReactNode),
            },
        ]);
        return { apiRef, columns: _columns };
    }

    const { apiRef, columns } = useApiRef();
    const handleEditRowsModelChange = React.useCallback((model: GridEditRowsModel) => {
            if (Object.keys(model).every(
                val => validateEmail(model[val].parent_email.value as string) || !model[val].parent_email.value
            )) {
                setAnchorEl(null);
                setcellError('');
                setEditRowsModel(model);
            } else {
                setcellError(STUDENTS_TABLE_EMAIL_ERROR);
            }
    }, []);
    if (studentsGet.status === 'success' && studentsGet.data && studentsGet.data.length > 0) {
        //props.setStudentsPresent(true);
        if (studentsGet.data.filter(propf=>propf.profile_type === 'student').length >= 40) {
            props.setIsAddStudentVisible(false);
        } else {
            props.setIsAddStudentVisible(true);
        };
        return (
            <>
                <DataGrid
                    autoHeight
                    columns={columns}
                    sortingOrder={['desc', 'asc']}
                    rows={studentsGet.data
                        .filter(val=>val.profile_type==='student')
                        .map((data: IProfile, index) => ({num: index + 1, ...data}))
                    }
                    editMode="row"
                    editRowsModel={editRowsModel}
                    onEditRowsModelChange={(model) => {
                        if (apiRef.current.getRowMode(Object.keys(model)[0]) === 'view') {
                            setEditRowsModel(model)
                        }
                        setAnchorEl(apiRef.current.getCellElement(Object.keys(model)[0], "parent_email"));
                        handleEditRowsModelChange(model);
                    }}
                    onRowEditCommit={
                        (id: GridRowId) => putStudent(id, apiRef.current.getEditRowsModel(), studentPut)
                    }
                    hideFooter={true}
                    rowHeight={56}
                    headerHeight={100}
                />
                <Popover
                    sx={{
                        pointerEvents: 'none',
                    }}
                    open={cellError? true: false}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    onClose={() => setAnchorEl(null)}
                    disableAutoFocus
                >
                    <Typography sx={{ p: 1 }}>
                        {cellError}
                    </Typography>
                </Popover>
                <DeleteStudentDialog
                    dialogOpen={dialogOpen}
                    idToDelete={idToDelete}
                    getStudentName={getStudentName}
                    setDialogOpen={setDialogOpen}
                    setIdToDelete={setIdToDelete}
                    studentDelete={studentDelete}
                />
            </>
        );
    } else if (studentsGet.status === 'success' && typeof studentsGet.data != 'undefined') {
        //props.setStudentsPresent(false);
        return <Typography>{STUDENTS_TABLE_EMPTY}</Typography>;
    } else if (studentsGet.status === 'loading') {
        return <CircularProgress sx={{alignSelf: 'center'}}/>;
    } else {
        props.setSyncState(true);
        return (
            <Button onClick={() => invalidate()}>
                {STUDENTS_TABLE_DATA_ERROR}
            </Button>
        );
    };
};